import { byId } from './index.ts';
import type { Category, Exercise } from '../types.ts';

/** Fixed publication schedule. Sep 28 retains the bridge day; two future days
 * were moved to Sep 26–27 at the user’s request. Append future dates explicitly. */
export const calendar: Record<string, Record<Category, string>> = {
  '2026-09-26': {
    reason: 'reason-beacon-times',
    math: 'math-marked-fish',
    reading: 'reading-timebank',
    foundations: 'foundations-plant-transport',
  },
  '2026-09-27': {
    reason: 'reason-stack-flips',
    math: 'math-walk-boundaries',
    reading: 'reading-moon-hoax',
    foundations: 'foundations-experiments',
  },

  '2026-09-28': {
    reason: 'reason-bridge',
    math: 'math-arrivals',
    reading: 'reading-maps',
    foundations: 'foundations-chemistry',
  },
  '2026-09-29': {
    reason: 'reason-eggs',
    math: 'math-dice',
    reading: 'reading-longitude',
    foundations: 'foundations-biology',
  },
  '2026-09-30': {
    reason: 'reason-ants',
    math: 'math-coins',
    reading: 'reading-plates',
    foundations: 'foundations-physics',
  },
  '2026-10-01': {
    reason: 'reason-bottles',
    math: 'math-sampling',
    reading: 'reading-locks',
    foundations: 'foundations-proportion',
  },
  '2026-10-02': {
    reason: 'reason-hats',
    math: 'math-screening',
    reading: 'reading-tides',
    foundations: 'foundations-earth',
  },
  '2026-10-03': {
    reason: 'reason-rooms',
    math: 'math-detectors',
    reading: 'reading-false-alarm',
    foundations: 'foundations-civics',
  },
  '2026-10-04': {
    reason: 'reason-runnerup',
    math: 'math-uploads',
    reading: 'reading-seed-vault',
    foundations: 'foundations-markets',
  },
  '2026-10-05': {
    reason: 'reason-numbercards',
    math: 'math-roundtrip',
    reading: 'reading-internet-archive',
    foundations: 'foundations-sampling',
  },
  '2026-10-06': {
    reason: 'reason-lamps',
    math: 'math-decay',
    reading: 'reading-braess',
    foundations: 'foundations-inheritance',
  },
  '2026-10-07': {
    reason: 'reason-workshop',
    math: 'math-river',
    reading: 'reading-molasses',
    foundations: 'foundations-arguments',
  },
  '2026-10-08': {
    reason: 'reason-flight-tickets',
    math: 'math-matching-cards',
    reading: 'reading-nyos',
    foundations: 'foundations-atoms',
  },
  '2026-10-09': {
    reason: 'reason-stone-game',
    math: 'math-dilution',
    reading: 'reading-mincemeat',
    foundations: 'foundations-ecosystems',
  },
  '2026-10-10': {
    reason: 'reason-fuel-loop',
    math: 'math-flight-height',
    reading: 'reading-ligo',
    foundations: 'foundations-light',
  },
  '2026-10-11': {
    reason: 'reason-grid-route',
    math: 'math-larger-die',
    reading: 'reading-fairphone',
    foundations: 'foundations-industrial-city',
  },
  '2026-10-12': {
    reason: 'reason-depot',
    math: 'math-cone-water',
    reading: 'reading-long-now',
    foundations: 'foundations-coordinates',
  },
  '2026-10-13': {
    reason: 'reason-deadlines',
    math: 'math-months',
    reading: 'reading-jevons',
    foundations: 'foundations-membranes',
  },
  '2026-10-14': {
    reason: 'reason-envelopes',
    math: 'math-transferred-ball',
    reading: 'reading-encrypt',
    foundations: 'foundations-circuits',
  },
  '2026-10-15': {
    reason: 'reason-jugs',
    math: 'math-two-payments',
    reading: 'reading-habakkuk',
    foundations: 'foundations-circulation',
  },
  '2026-10-16': {
    reason: 'reason-dominoes',
    math: 'math-collecting-types',
    reading: 'reading-dabbawala',
    foundations: 'foundations-reformation',
  },
  '2026-10-17': {
    reason: 'reason-route-pass',
    math: 'math-pooled-spread',
    reading: 'reading-great-stink',
    foundations: 'foundations-distributions',
  },
  '2026-10-18': {
    reason: 'reason-inspection-lines',
    math: 'math-first-success-position',
    reading: 'reading-biosphere',
    foundations: 'foundations-heat',
  },
  '2026-10-19': {
    reason: 'reason-batch-merge',
    math: 'math-repair-repeat',
    reading: 'reading-openstreetmap',
    foundations: 'foundations-selection',
  },
  '2026-10-20': {
    reason: 'reason-seat-blocks',
    math: 'math-rectangle-fence',
    reading: 'reading-kowloon',
    foundations: 'foundations-trade',
  },
  '2026-10-21': {
    reason: 'reason-liar-cards',
    math: 'math-bus-mixture',
    reading: 'reading-dunbar',
    foundations: 'foundations-clauses',
  },
  '2026-10-22': {
    reason: 'reason-cache-checks',
    math: 'math-rational-fill',
    reading: 'reading-pneumatic',
    foundations: 'foundations-watersheds',
  },
  '2026-10-23': {
    reason: 'reason-shelf-swaps',
    math: 'math-random-cut',
    reading: 'reading-peigneur',
    foundations: 'foundations-acid-base',
  },
  '2026-10-24': {
    reason: 'reason-pack-order',
    math: 'math-line-fit',
    reading: 'reading-mpesa',
    foundations: 'foundations-sound',
  },
  '2026-10-25': {
    reason: 'reason-party-guarantee',
    math: 'math-sound-level',
    reading: 'reading-mechanical-turk',
    foundations: 'foundations-indian-ocean',
  },
};
export const publicationDates = Object.keys(calendar).sort();
export function dateForExercise(exerciseId: string): string | undefined {
  return publicationDates.find((date) => Object.values(calendar[date]).includes(exerciseId));
}
export function forDate(date: string, category: Category): Exercise | undefined {
  const id = calendar[date]?.[category];
  return id ? byId[id] : undefined;
}

/** One-time pilot reschedule; used to retain attempts and drafts, not backdate streaks. */
export const pilotDateMoves: { from: string; to: string }[] = [
  // Read the original snapshot; destination records always win.
  { from: '2026-10-26', to: '2026-09-26' },
  { from: '2026-10-27', to: '2026-09-27' },
  { from: '2026-09-27', to: '2026-09-28' },
  { from: '2026-09-25', to: '2026-09-29' },
  { from: '2026-09-26', to: '2026-09-30' },
  { from: '2026-09-28', to: '2026-10-01' },
  { from: '2026-09-29', to: '2026-10-02' },
  { from: '2026-09-30', to: '2026-10-03' },
  { from: '2026-10-01', to: '2026-10-04' },
  { from: '2026-10-02', to: '2026-10-05' },
  { from: '2026-10-03', to: '2026-10-06' },
  { from: '2026-10-04', to: '2026-10-07' },
  { from: '2026-10-05', to: '2026-10-08' },
  { from: '2026-10-06', to: '2026-10-09' },
  { from: '2026-10-07', to: '2026-10-10' },
  { from: '2026-10-08', to: '2026-10-11' },
  { from: '2026-10-09', to: '2026-10-12' },
  { from: '2026-10-10', to: '2026-10-13' },
  { from: '2026-10-11', to: '2026-10-14' },
  { from: '2026-10-12', to: '2026-10-15' },
  { from: '2026-10-13', to: '2026-10-16' },
  { from: '2026-10-14', to: '2026-10-17' },
  { from: '2026-09-24', to: '2026-09-29' },
  { from: '2026-09-25', to: '2026-09-30' },
  { from: '2026-09-26', to: '2026-09-28' },
  { from: '2026-09-27', to: '2026-10-01' },
  { from: '2026-09-28', to: '2026-10-02' },
  { from: '2026-09-21', to: '2026-09-29' },
  { from: '2026-09-22', to: '2026-09-30' },
  { from: '2026-09-23', to: '2026-09-28' },
  { from: '2026-09-24', to: '2026-10-01' },
  { from: '2026-09-25', to: '2026-10-02' },
];
